import { Card } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Wallet, Target, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AlertCounts {
  yellow: number;  // +15% - Recover 20%
  orange: number;  // +30% - Recover 40%
  red: number;     // +45% - Recover 100%
}

interface PortfolioStatsProps {
  totalInvested: number;
  totalValue: number;
  totalValueCHF: number;
  totalInvestedCHF: number;
  totalAddedValueCHF: number;
  initialTotalInvestment: number;
  totalGain: number;
  totalGainPct: number;
  positionCount: number;
  alertCounts: AlertCounts;
}

export function PortfolioStats({ 
  totalInvested, 
  totalValue,
  totalValueCHF,
  totalInvestedCHF,
  totalAddedValueCHF,
  initialTotalInvestment,
  totalGain, 
  totalGainPct,
  positionCount,
  alertCounts
}: PortfolioStatsProps) {
  const isGain = totalGainPct >= 0;
  const totalAlerts = alertCounts.yellow + alertCounts.orange + alertCounts.red;
  
  return (
    <div className="space-y-4 mb-8">
      {/* Alert Indicators */}
      {totalAlerts > 0 && (
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">Active Sell Alerts</span>
          </div>
          <div className="flex flex-wrap gap-3">
            {alertCounts.red > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-red-500/10 rounded-lg border-2 border-red-500 animate-pulse">
                <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                <span className="text-sm font-bold text-red-500">🎯 100% Recovery</span>
                <span className="text-sm font-semibold text-foreground">{alertCounts.red} positions</span>
              </div>
            )}
            {alertCounts.orange > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-orange-500/10 rounded-lg border-2 border-orange-500 animate-pulse">
                <div className="w-3 h-3 rounded-full bg-orange-500 animate-pulse" />
                <span className="text-sm font-bold text-orange-500">⚡ 40% Recovery</span>
                <span className="text-sm font-semibold text-foreground">{alertCounts.orange} positions</span>
              </div>
            )}
            {alertCounts.yellow > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-yellow-500/10 rounded-lg border-2 border-yellow-500 animate-pulse">
                <div className="w-3 h-3 rounded-full bg-yellow-500 animate-pulse" />
                <span className="text-sm font-bold text-yellow-500">💰 20% Recovery</span>
                <span className="text-sm font-semibold text-foreground">{alertCounts.yellow} positions</span>
              </div>
            )}
          </div>
        </Card>
      )}
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Wallet className="h-5 w-5 text-primary" />
          </div>
          <span className="text-sm font-medium text-muted-foreground">Total Invested</span>
        </div>
        <p className="text-2xl font-bold text-foreground">
          {totalValueCHF.toLocaleString()} CHF
        </p>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-accent/10 rounded-lg">
            <TrendingUp className="h-5 w-5 text-accent" />
          </div>
          <span className="text-sm font-medium text-muted-foreground">Total Added Value</span>
        </div>
        <p className={cn(
          "text-2xl font-bold",
          totalAddedValueCHF >= 0 ? "text-foreground" : "text-red-500"
        )}>
          {totalAddedValueCHF.toLocaleString()} CHF
        </p>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-secondary/10 rounded-lg">
            <Target className="h-5 w-5 text-secondary" />
          </div>
          <span className="text-sm font-medium text-muted-foreground">Initial Total Investment</span>
        </div>
        <p className="text-2xl font-bold text-foreground">
          {initialTotalInvestment.toLocaleString()} CHF
        </p>
      </Card>
      
      <Card className={cn(
        "p-6"
      )}>
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-muted rounded-lg">
            <TrendingUp className="h-5 w-5 text-muted-foreground" />
          </div>
          <span className="text-sm font-medium text-muted-foreground">Positions</span>
        </div>
        <p className="text-2xl font-bold text-foreground">
          {positionCount}
        </p>
      </Card>
      </div>
    </div>
  );
}
